import { get, clamp, isEmpty, size, floor, slice, min } from 'lodash'
import queryString from 'query-string'

export const DEFAULT_SETTINGS = {
  page: 0,
  offset: 100,
}


/**
 * Instantiate a Pagination Coordinator that uses the url query parameters as the source of truth
 * @param {Array} subject - The data set to be paginated.
 *
 * @param {Object} navigation - The navigation object.
 * @param {function} navigation.push - The method to push to the next URL.
 * @param {function} navigation.replace - The method to replace the URL.
 *
 * @param {Object} navigation.location - The location information.
 * @param {string} navigation.location.search - The search query to be decoded.
 * @param {string} navigation.location.pathname - The current pathname.
 *
 * @param {Object} [userSettings] - The custom settings provided by the user.
 * @param {number} [userSettings.page=0] - The default page number.
 * @param {number} [userSettings.offset=10] - The default offset.
 */

function paginationCoordinator(subject, navigation, userSettings) {
  // internal
  const settings = { ...DEFAULT_SETTINGS, ...userSettings }
  const search = get(navigation, 'location.search', '')
  const pathname = get(navigation, 'location.pathname', '')
  const replace = get(navigation, 'replace', () => {})
  const push = get(navigation, 'push', () => {})
  const queryParams = queryString.parse(search)
  const pageParam = get(queryParams, 'page', settings.page)
  const offsetParam = get(queryParams, 'offset', settings.offset)
  const empty = isEmpty(queryParams)
  const commitAction = empty ? push : replace

  const commitChanges = (page, offset) => {
    const search = queryString.stringify({ page, offset })

    commitAction({ pathname, search })
  }

  // public
  const page = clamp(parseInt(pageParam), 0, 1000)
  const offset = clamp(parseInt(offsetParam), 1, 1000)
  const count = size(subject)
  const minPage = 0
  const maxPage = floor(count / offset)
  const startIndex = page * offset
  const endIndex = min([startIndex + offset, count])
  const start = startIndex + 1
  const end = endIndex
  const result = slice(subject, startIndex, endIndex)

  const goToPage = (currentPage, currentOffset) => {
    commitChanges(currentPage, currentOffset)
  }

  const previousPage = () => {
    commitChanges(clamp(page - 1, minPage, maxPage), offset)
  }

  const nextPage = () => {
    commitChanges(clamp(page + 1, minPage, maxPage), offset)
  }

  const updateOffset = (nextOffset) => {
    commitChanges(0, clamp(nextOffset, 1, 1000))
  }

  const updatePage = (nextPage) => {
    commitChanges(nextPage, offset)
  }

  const reset = () => {
    commitChanges()
  }

  return {
    page,
    offset,
    start,
    end,
    startIndex,
    endIndex,
    result,
    count,
    nextPage,
    previousPage,
    updateOffset,
    updatePage,
    goToPage,
    reset,
  }
}

export default paginationCoordinator
